import React, { useState } from 'react';
import { Button, Modal, ModalBody } from 'reactstrap';
import '../../assets/css/--component-rubric.css';

/**
 * What would change, section by section, kept or skipped one at a time.
 *
 * Built once and used by everything that proposes a change to a prompt: the
 * "ask AI to change this" dialog today, and the findings from real calls when
 * idea 07 lands. It takes a list of `{ section, before, after, why, asked }`
 * and knows nothing about where they came from — which is the whole reason
 * 07 is cheap.
 *
 * "Keep this" and "Skip it", not "Accept" and "Reject". The customer is
 * choosing what they want, not grading the model's homework.
 *
 * Anything the model offered without being asked arrives unticked. It is
 * shown rather than hidden, so it can be taken if it is good, and nothing
 * slides in unseen.
 */
const PromptDiff = ({ open, changes, onCancel, onKeep, saving }) => {
  const [kept, setKept] = useState(() => changes.map((c) => c.asked !== false));

  // The list changes each time the dialog is opened with a new answer.
  const [seen, setSeen] = useState(changes);
  if (seen !== changes) {
    setSeen(changes);
    setKept(changes.map((c) => c.asked !== false));
  }

  const keptCount = kept.filter(Boolean).length;

  return (
    <Modal isOpen={open} toggle={onCancel} centered size="lg" scrollable>
      <ModalBody className="p-4">
        <h5 className="mb-1">{changes.length === 1 ? 'One section would change' : `${changes.length} sections would change`}</h5>
        <p className="fs-14 text-muted">Nothing is saved until you choose.</p>

        {changes.map((change, index) => (
          <div className={`prompt-diff ${kept[index] ? 'prompt-diff-kept' : ''}`} key={`${change.section}-${index}`}>
            <div className="d-flex justify-content-between align-items-center">
              <strong>{change.section}</strong>
              <div className="d-flex gap-2">
                <Button
                  size="sm"
                  color={kept[index] ? 'success' : 'light'}
                  onClick={() => setKept(kept.map((k, i) => (i === index ? true : k)))}
                >
                  Keep this
                </Button>
                <Button
                  size="sm"
                  color={kept[index] ? 'light' : 'secondary'}
                  onClick={() => setKept(kept.map((k, i) => (i === index ? false : k)))}
                >
                  {kept[index] ? 'Skip it' : 'Skipped'}
                </Button>
              </div>
            </div>

            {change.asked === false && (
              <p className="prompt-diff-offered">
                You did not ask for this one. It is off unless you want it.
              </p>
            )}

            {change.before ? (
              <p className="prompt-diff-before">{change.before}</p>
            ) : (
              <p className="prompt-diff-before prompt-diff-empty">This section is not in the prompt yet.</p>
            )}
            <p className="prompt-diff-after">{change.after}</p>
            {change.why && <p className="prompt-diff-why"><strong>Why:</strong> {change.why}</p>}
          </div>
        ))}

        <div className="d-flex justify-content-between align-items-center mt-3">
          <span className="fs-13 text-muted">
            Skip everything and your prompt is left exactly as it was.
          </span>
          <div className="d-flex gap-2">
            <Button color="light" onClick={onCancel} disabled={saving}>Cancel</Button>
            <Button
              color="primary"
              disabled={saving || keptCount === 0}
              onClick={() => onKeep(changes.filter((_, i) => kept[i]))}
            >
              {saving ? 'Saving…' : `Keep ${keptCount} change${keptCount === 1 ? '' : 's'}`}
            </Button>
          </div>
        </div>
      </ModalBody>
    </Modal>
  );
};

export default PromptDiff;
